import React from 'react';
import { motion } from 'framer-motion';
import { ATELIER, FONTS } from '../../tokens';
import { SmartComposer } from '../SmartComposer';

/**
 * FloatingComposer - centered glass card pinned to the bottom of the main
 * column in Bento/Home mode. Not literally fixed-position; it's a flex
 * footer so it plays well with the sidebar and doesn't overlap.
 *
 * Anatomy (top to bottom):
 *   • 1px aurora ribbon (breathing gradient - the AI "heartbeat")
 *   • Card: rounded 20, hairline border, multi-layer violet shadow, white
 *   • The real <SmartComposer embedded /> inside
 *   • Tiny mono signoff below
 */
export function FloatingComposer() {
  const [isMobile, setIsMobile] = React.useState(window.innerWidth < 768);
  React.useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <div style={{
      flexShrink: 0,
      padding: isMobile ? '8px 10px 14px' : '8px 24px 22px',
      background: 'transparent',
      position: 'relative',
      zIndex: 5,
    }}>
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1], delay: 0.35 }}
        style={{
          maxWidth: 760,
          margin: '0 auto',
          position: 'relative',
        }}
      >
        {/* Aurora ribbon */}
        <div aria-hidden style={{
          position: 'absolute',
          top: -1, left: 20, right: 20,
          height: 1,
          overflow: 'hidden',
          pointerEvents: 'none',
        }}>
          <div className="dgw-aurora" style={{ width: '100%', height: '100%' }} />
        </div>

        {/* The card */}
        <div style={{
          background: ATELIER.bg,
          border: `1px solid ${ATELIER.border}`,
          borderRadius: 20,
          boxShadow:
            '0 20px 48px -14px rgba(124,58,237,0.22), ' +
            '0 6px 14px -4px rgba(124,58,237,0.10), ' +
            '0 1px 2px rgba(10,10,20,0.04)',
          position: 'relative',
          overflow: 'visible',
        }}>
          {/* Soft violet inner top-glow */}
          <div aria-hidden style={{
            position: 'absolute',
            top: 0, left: 0, right: 0,
            height: 60,
            background: 'radial-gradient(ellipse 80% 100% at 50% 0%, rgba(167,139,250,0.09) 0%, transparent 70%)',
            pointerEvents: 'none',
            borderRadius: '20px 20px 0 0',
          }} />

          <div style={{ padding: isMobile ? '8px 8px 6px' : '14px 18px 10px', position: 'relative' }}>
            <SmartComposer embedded />
          </div>
        </div>

        {/* Signoff */}
        <div style={{
          textAlign: 'center',
          marginTop: 8,
          fontSize: 10.5, color: ATELIER.inkMuted,
          fontFamily: FONTS.mono, letterSpacing: '0.18em',
          fontWeight: 600, textTransform: 'uppercase',
        }}>
          <span style={{ color: ATELIER.accent }}>·</span>&nbsp;
          DAGGPT can make mistakes · Outputs anchored on DAGCHAIN
        </div>
      </motion.div>
    </div>
  );
}
